import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../hooks/useOnFarmActionBlocked';
import { BackButton, SelectStorageItems } from '../../../components';
import { SelectedSpecimenQuantity } from '../../../components/specimen-tables/SelectSpecimens';
import SelectSpecimenFromCanisterTable from '../../../components/select-specimen-from-canister/SelectSpecimenFromCanisterTable';
import { AlertSVG } from '../../../components/svgs';
import { selectOption } from '../../../types/interfaces';
import { OnFarmMoveRequestBody, OnFarmMoveSpecimenRequest } from '../../../types/interfaces/onFarmTransaction.interfaces';
import { StorageTankOptions } from '../../../types/enums/storageTank.enum';
import { TransactionTypeEnum } from '../../../types/enums/transaction.enum';
import { requestOnFarmMoveInventory } from '../../../api/onFarmTransactionApi';
import { showToast } from '../../../services/toast.service';
import { downloadOnFarmTransactionExport } from '../../../services/onFarmTransactionExport.service';
import { redirectToTopOfThePage } from '../../../utils/commonUtils';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { toastMessages } from '../../../constants/errorMessages';
import { PAGE_HEADER_CONSTANTS, LABEL_CONSTANTS, BUTTON_CONSTANTS, VALIDATION_ALERT_CONSTANTS } from '../../../constants/common';
export interface MoveManagementBaseAutoFill {
  site: selectOption;
  tank: selectOption;
  canister: selectOption;
  selectedSpecimenId: number;
}

interface SelectedQuantitiesState {
  valid: boolean;
  value: SelectedSpecimenQuantity[];
}

const OnFarmMove = (): JSX.Element => {
  const { state: autoFill } = useLocation();
  const navigate = useNavigate();

  const [movingFromSite, setMovingFromSite] = useState<selectOption>(autoFill?.site ?? { value: '', name: '' });
  const [movingFromTank, setMovingFromTank] = useState<selectOption>(autoFill?.tank ?? { value: '', name: '' });
  const [movingFromCanister, setMovingFromCanister] = useState<selectOption>(autoFill?.canister ?? { value: '', name: '' });

  const [movingToSite, setMovingToSite] = useState<selectOption>({ value: '', name: '' });
  const [movingToTank, setMovingToTank] = useState<selectOption>({ value: '', name: '' });
  const [movingToCanister, setMovingToCanister] = useState<selectOption>({ value: '', name: '' });

  const [notes, setNotes] = useState('');
  const [selectedQuantities, setSelectedQuantities] = useState<SelectedQuantitiesState>({
    valid: false,
    value: [],
  });

  const { accountId } = useAppSelector(state => state.activeAccount);
  const isOnFarmLocked = useOnFarmActionBlocked();

  const isSameCanister =
    !!movingFromCanister.value && !!movingToCanister.value && movingFromCanister.value === movingToCanister.value;

  const isValid = () => {
    return (
      movingFromCanister.value.length &&
      movingToCanister.value.length &&
      !isSameCanister &&
      selectedQuantities.value.length &&
      selectedQuantities.valid
    );
  };

  const getSelectedSpecimens = (): OnFarmMoveSpecimenRequest[] => {
    const embryoSpecimens = selectedQuantities.value.filter(selectedQuantity => selectedQuantity.embryoStrawId);
    const nonEmbryoSpecimens = selectedQuantities.value.filter(selectedQuantity => !selectedQuantity.embryoStrawId);

    const groupedEmbryoSpecimens = embryoSpecimens.reduce((grouped, selectedQuantity) => {
      const onFarmSpecimenId = selectedQuantity.specimenId;

      if (!grouped[onFarmSpecimenId]) {
        grouped[onFarmSpecimenId] = {
          onFarmSpecimenId,
          quantity: 0,
          selectedEmbryoStrawIds: [],
          selectedCanisterId: Number(movingFromCanister.value),
          destinationCanisterId: Number(movingToCanister.value),
        };
      }

      grouped[onFarmSpecimenId].quantity += selectedQuantity.quantity;
      grouped[onFarmSpecimenId].selectedEmbryoStrawIds!.push(selectedQuantity.embryoStrawId!);

      return grouped;
    }, {} as Record<number, OnFarmMoveSpecimenRequest>);

    const otherSpecimens: OnFarmMoveSpecimenRequest[] = nonEmbryoSpecimens.map(selectedQuantity => ({
      onFarmSpecimenId: selectedQuantity.specimenId,
      quantity: selectedQuantity.quantity,
      selectedCanisterId: Number(movingFromCanister.value),
      destinationCanisterId: Number(movingToCanister.value),
    }));

    return [...Object.values(groupedEmbryoSpecimens), ...otherSpecimens];
  };

  const clearPageValues = () => {
    setMovingFromSite({ value: '', name: '' });
    setMovingFromTank({ value: '', name: '' });
    setMovingFromCanister({ value: '', name: '' });
    setMovingToSite({ value: '', name: '' });
    setMovingToTank({ value: '', name: '' });
    setMovingToCanister({ value: '', name: '' });
    setNotes('');
    setSelectedQuantities({ valid: false, value: [] });
  };

  const handleSubmit = async () => {
    if (isOnFarmLocked) {
      return;
    }
    if (isSameCanister) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    const moveSpecimensRequestBody: OnFarmMoveRequestBody = { specimens: getSelectedSpecimens(), notes };
    try {
      const { data: onFarmTransactionId } = await requestOnFarmMoveInventory(moveSpecimensRequestBody);
      await downloadOnFarmTransactionExport(
        {
          onFarmTransactionId,
          specimens: moveSpecimensRequestBody.specimens,
        },
        TransactionTypeEnum.Move,
      );
      showToast.success(toastMessages.TRANSACTION_SUCCESS);
      if (autoFill) {
        navigate(ROUTE_PATHS.APP_ON_FARM_MANAGEMENT_CANISTER_CONTENTS, { state: autoFill });
        return;
      }
      clearPageValues();
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  return (
    <div className="inventory-management inventory-management-move">
      {autoFill && (
        <BackButton onClick={() => navigate(ROUTE_PATHS.APP_ON_FARM_MANAGEMENT_CANISTER_CONTENTS, { state: autoFill })} />
      )}

      <div className="inventory-action card">
        <h1>{PAGE_HEADER_CONSTANTS.ON_FARM_MANAGEMENT_MOVE}</h1>

        <h2>{LABEL_CONSTANTS.MOVING_FROM}:</h2>

        <SelectStorageItems
          accountId={accountId}
          selectedSite={movingFromSite}
          selectedTank={movingFromTank}
          selectedCanister={movingFromCanister}
          setSelectedSite={setMovingFromSite}
          setSelectedTank={setMovingFromTank}
          setSelectedCanister={setMovingFromCanister}
          storageTankOptions={StorageTankOptions.Both}
          activeLocationsOnly
          disabled={isOnFarmLocked}
        />

        <h2>{LABEL_CONSTANTS.INVENTORY}:</h2>

        {movingFromCanister.value && (
          <SelectSpecimenFromCanisterTable
            canisterId={Number(movingFromCanister.value)}
            onChange={setSelectedQuantities}
            selectedSpecimenId={autoFill?.selectedSpecimenId}
            accountId={accountId}
            disabled={isOnFarmLocked}
          />
        )}

        <h2>{LABEL_CONSTANTS.MOVING_TO}</h2>
        <SelectStorageItems
          accountId={accountId}
          selectedSite={movingToSite}
          selectedTank={movingToTank}
          selectedCanister={movingToCanister}
          setSelectedSite={setMovingToSite}
          setSelectedTank={setMovingToTank}
          setSelectedCanister={setMovingToCanister}
          storageTankOptions={StorageTankOptions.Both}
          activeLocationsOnly
          disabled={isOnFarmLocked}
        />

        {isSameCanister && (
          <div className="validated-input-message-error">
            <AlertSVG />
            {VALIDATION_ALERT_CONSTANTS.SAME_SOURCE_AND_DESTINATION_CANISTER}
          </div>
        )}

        <div className="form-row expanded-text-area">
          <label htmlFor="notes">{LABEL_CONSTANTS.NOTES}</label>
          <textarea
            id="notes"
            name="notes"
            placeholder="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            cols={50}
          />
        </div>
        <br />

        <br />
        <div className="submit-button">
          <button
            className="button green small"
            disabled={isOnFarmLocked || !isValid()}
            onClick={() => {
              handleSubmit();
              redirectToTopOfThePage();
            }}>
            {BUTTON_CONSTANTS.SUBMIT}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnFarmMove;
